import type { CoverageFieldErrors } from '@/features/quotes/components/capital-global/steps/sections/coverages-table.component';

type ValidationIssue = {
  path: ReadonlyArray<PropertyKey>;
  message: string;
};

export type StepCoveragesVisibleErrors = {
  coverageErrors: CoverageFieldErrors;
  /** Erro por assistência standalone (code → mensagem). Alimentado pelo
   *  conflito de exclusão mútua, que pode acusar um toggle. */
  assistanceErrors?: Record<string, string | undefined>;
  capitalizationError?: string;
  assistanceFuneralError?: string;
  /** Attach/API validation that does not map to a specific field. */
  generalError?: string;
};

export function buildStepCoveragesErrors(
  issues: ValidationIssue[]
): StepCoveragesVisibleErrors {
  const coverageErrors: CoverageFieldErrors = {};
  let capitalizationError: string | undefined;
  let assistanceFuneralError: string | undefined;

  for (const issue of issues) {
    const [root, rowId, field] = issue.path;

    if (root === 'coverages' && typeof rowId === 'string') {
      const row = coverageErrors[rowId] ?? {};
      if (field === 'maxIndividualCapital' && !row.maxIndividualCapital) {
        row.maxIndividualCapital = issue.message;
      }
      if (field === 'percent' && !row.percent) {
        row.percent = issue.message;
      }
      // `selection` cobre o que não é campo de valor: relação/exclusividade,
      // base proporcional não contratada e "Morte deve estar habilitada". Sem
      // isto essas mensagens só apareciam no toast, nunca na linha.
      if ((field === 'selection' || field === 'enabled') && !row.selection) {
        row.selection = issue.message;
      }
      coverageErrors[rowId] = row;
      continue;
    }

    if (root === 'capitalization' && !capitalizationError) {
      capitalizationError = issue.message;
      continue;
    }

    if (root === 'assistanceFuneral' && !assistanceFuneralError) {
      assistanceFuneralError = issue.message;
    }
  }

  return { coverageErrors, capitalizationError, assistanceFuneralError };
}

/**
 * Collects unique, human-readable validation messages from zod issues.
 *
 * Used to feed the full error detail into a toast (above the "Próximo" button),
 * keeping the inline field markers concise so long messages don't break the layout.
 */
export function collectStepCoveragesErrorMessages(
  issues: ValidationIssue[]
): string[] {
  const seen = new Set<string>();
  const messages: string[] = [];

  for (const issue of issues) {
    const message = issue.message.trim();
    if (!message || seen.has(message)) continue;
    seen.add(message);
    messages.push(message);
  }

  return messages;
}
